import React, { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, MapPin, Search } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Autocomplete ville française avec code postal via Mapbox Geocoding.
 * Affiche un dropdown sous l'input avec les suggestions trouvées.
 *
 * Props:
 *   value          : { city, postal_code, lat, lng } | null
 *   onChange       : (value) => void  // appelé quand l'utilisateur tape OU sélectionne
 *   disabled       : bool
 *   label, testId
 */
export default function CityAutocomplete({ value, onChange, disabled, label = "Ville base", testId = "city-autocomplete" }) {
  const [query, setQuery] = useState(value?.city || "");
  const [postalCode, setPostalCode] = useState(value?.postal_code || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Synchronise quand le parent change la valeur (chargement initial)
  useEffect(() => {
    setQuery(value?.city || "");
    setPostalCode(value?.postal_code || "");
  }, [value?.city, value?.postal_code]);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (q) => {
    if (!MAPBOX_TOKEN) return;
    if (!q || q.trim().length < 2) { setSuggestions([]); return; }
    setLoading(true);
    // types=place,postcode → renvoie villes ET codes postaux français
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
      `?country=fr&types=place,postcode,locality&language=fr&autocomplete=true&limit=6&access_token=${MAPBOX_TOKEN}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const feats = (data?.features || []).map((f) => {
          // Extrait le code postal depuis le contexte si la feature est une "place"
          const ctx = f.context || [];
          const placeType = (f.place_type || [])[0];
          let city = f.text;
          let postcode = "";
          if (placeType === "postcode") {
            postcode = f.text;
            // Cherche le nom de la ville dans le contexte
            const placeCtx = ctx.find((c) => c.id?.startsWith("place")) ||
                             ctx.find((c) => c.id?.startsWith("locality"));
            city = placeCtx?.text || f.place_name?.split(",")[1]?.trim() || f.text;
          } else {
            const pcCtx = ctx.find((c) => c.id?.startsWith("postcode"));
            postcode = pcCtx?.text || "";
          }
          const region = ctx.find((c) => c.id?.startsWith("region"))?.text || "";
          return {
            id: f.id,
            city,
            postal_code: postcode,
            region,
            lat: f.center?.[1],
            lng: f.center?.[0],
          };
        }).filter((s) => s.city && s.lat && s.lng);
        setSuggestions(feats);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    // Optimiste : on remonte au parent l'état partiel (ville en cours, plus de coords valides)
    onChange?.({ city: v, postal_code: postalCode, lat: null, lng: null });
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  };

  const onPostalInput = (e) => {
    const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    setPostalCode(v);
    onChange?.({ city: query, postal_code: v, lat: value?.lat || null, lng: value?.lng || null });
  };

  const pick = async (s) => {
    setQuery(s.city);
    setShowDropdown(false);
    // Si pas de code postal dans le résultat (cas fréquent pour type=place),
    // on fait une requête de géocodage inverse pour le récupérer
    let postcode = s.postal_code || "";
    if (!postcode && s.lat && s.lng) {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${s.lng},${s.lat}.json` +
          `?country=fr&types=postcode&language=fr&limit=1&access_token=${MAPBOX_TOKEN}`;
        const r = await fetch(url);
        const d = await r.json();
        postcode = d?.features?.[0]?.text || "";
      } catch (_) { /* silent fallback */ }
    }
    setPostalCode(postcode);
    onChange?.({ city: s.city, postal_code: postcode, lat: s.lat, lng: s.lng });
  };

  const isResolved = !!value?.lat && !!value?.lng;

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label className="overline">{label}</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              type="text"
              value={query}
              onChange={onInput}
              onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
              disabled={disabled}
              placeholder="Tapez le nom de votre ville…"
              className="pl-9 pr-9 h-11 rounded-md"
              data-testid={`${testId}-input`}
              autoComplete="off"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-500" />
            )}
            {!loading && isResolved && (
              <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
            )}
          </div>
        </div>
        <div>
          <Label className="overline">Code postal</Label>
          <Input
            type="text"
            inputMode="numeric"
            value={postalCode}
            onChange={onPostalInput}
            disabled={disabled}
            placeholder="12000"
            maxLength={5}
            className="mt-1 h-11 rounded-md font-mono"
            data-testid={`${testId}-postal`}
          />
        </div>
      </div>
      {isResolved && (
        <p className="text-xs text-neutral-500 mt-1">
          GPS : {Number(value.lat).toFixed(3)}, {Number(value.lng).toFixed(3)} <span className="text-emerald-600">✓</span>
        </p>
      )}
      {!isResolved && query && !loading && (
        <p className="text-xs text-amber-700 mt-1">
          Choisissez une suggestion dans la liste pour valider la localisation.
        </p>
      )}

      {showDropdown && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-md shadow-lg max-h-72 overflow-auto"
          data-testid={`${testId}-dropdown`}>
          {loading && suggestions.length === 0 && (
            <div className="p-3 text-sm text-neutral-500 font-mono">Recherche…</div>
          )}
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 hover:bg-[#1F4032]/5 border-b border-neutral-100 last:border-0 transition-colors"
              data-testid={`${testId}-option-${s.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.city}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {s.region || "France"}
                  </div>
                </div>
                {s.postal_code && (
                  <span className="text-xs font-mono px-2 py-0.5 bg-[#1F4032] text-white rounded shrink-0">
                    {s.postal_code}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
